/** Cliente HTTP da SPA. Sessão por cookie HttpOnly (mesmo origin /api) + header CSRF em mutações. */
export class ApiError extends Error {
  status: number;
  code: string;
  details?: Record<string, any>;
  requestId?: string;
  constructor(status: number, code: string, message: string, details?: Record<string, any>, requestId?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const f of (this.details?.fields as Array<{ path: string; message: string }>) ?? []) out[f.path] = f.message;
    return out;
  }
}

let csrfToken: string | null = null;
export function setCsrf(t: string | null) {
  csrfToken = t;
}

const BASE = import.meta.env.VITE_API_BASE_URL && import.meta.env.VITE_API_BASE_URL !== "/api" ? import.meta.env.VITE_API_BASE_URL.replace(/\/api$/, "") : "";

export async function request<T = any>(method: string, path: string, body?: unknown, init: { headers?: Record<string, string>; signal?: AbortSignal } = {}): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json", ...(init.headers ?? {}) };
  if (body !== undefined) headers["content-type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["x-csrf-token"] = csrfToken;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: "same-origin", signal: init.signal });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiError(0, "network_error", "Sem conexão com o servidor. Verifique sua rede e tente novamente.");
  }
  const text = await res.text();
  let data: any = null;
  let jsonOk = true;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
      jsonOk = false;
    }
  }
  if (!res.ok) {
    throw new ApiError(res.status, data?.code ?? "http_error", data?.message ?? `Erro ${res.status}`, data?.details, data?.request_id);
  }
  // Resposta 200 que não é JSON = normalmente o proxy de /api devolveu o index.html da SPA
  // (nginx sem a rota /api -> API). Falha explícita em vez de devolver HTML como "dado".
  if (!jsonOk) {
    throw new ApiError(res.status, "invalid_response", "Resposta inesperada do servidor: /api não parece estar apontando para a API (verifique o proxy do nginx).");
  }
  return data as T;
}

export const api = {
  get: <T = any>(p: string, signal?: AbortSignal) => request<T>("GET", p, undefined, { signal }),
  post: <T = any>(p: string, b?: unknown, headers?: Record<string, string>) => request<T>("POST", p, b ?? {}, { headers }),
  patch: <T = any>(p: string, b?: unknown) => request<T>("PATCH", p, b ?? {}),
  put: <T = any>(p: string, b?: unknown) => request<T>("PUT", p, b ?? {}),
  del: <T = any>(p: string) => request<T>("DELETE", p),
};

/** Chave de idempotência para ações de criação/monetárias disparadas pela UI. */
export function idemKey(): string {
  return crypto.randomUUID();
}

export function qs(params: Record<string, string | number | boolean | null | undefined | string[]>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) v.forEach((x) => u.append(k, x));
    else u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}
